/**
 * How the engine touches media: one chunk at a time, never a file (FEAT-038
 * wave 2).
 *
 * Everything an upload needs from a moment's bytes goes through here — the
 * PATCH bodies and the checksum the server verifies at finalize. Both are fed
 * from ~2 MiB reads, so a 12 MB photo and a 400 MB clip cost the same memory.
 *
 * The checksum used to be `crypto.subtle.digest('SHA-256', await
 * blob.arrayBuffer())` — the whole file resident, at the end of an upload that
 * had just carefully avoided being resident. `hash-wasm` hashes incrementally
 * AND can save its state mid-stream, so a relaunch resumes the checksum at the
 * same chunk the upload resumes at instead of re-reading everything behind it.
 */
import { createSHA256, type IHasher } from 'hash-wasm';
import { MEDIA_CHUNK_BYTES, mediaStore } from '../media/mediaStore';

/**
 * A moment's bytes, readable in chunk-sized pieces without ever assembling
 * them. `readAt` returns AT MOST one chunk and never spans a boundary, so the
 * caller's memory is bounded whatever it asks for.
 */
export interface ChunkSource {
  readonly size: number;
  readAt(offset: number): Promise<Uint8Array | null>;
}

/** The normal source: chunk rows in `momento-media`. */
export function mediaSource(mediaId: string, size: number): ChunkSource {
  return {
    size,
    readAt: (offset) => mediaStore.readAt(mediaId, offset),
  };
}

/**
 * A Blob that is already in hand — the migration's path for a record the
 * evacuation could not thin (quota, a torn copy). A Blob slice is lazy, so
 * this is still one chunk of memory above whatever backs the blob itself, and
 * it means such a row can send without first being copied a second time onto a
 * phone that is already full.
 */
export function blobSource(blob: Blob): ChunkSource {
  return {
    size: blob.size,
    async readAt(offset) {
      if (offset >= blob.size) return null;
      // Aligned to the same boundaries as the chunk store, so a resume reads
      // identical bodies whichever source it came back on.
      const seq = Math.floor(offset / MEDIA_CHUNK_BYTES);
      const end = Math.min((seq + 1) * MEDIA_CHUNK_BYTES, blob.size);
      const piece = await blob.slice(offset, end).arrayBuffer();
      return new Uint8Array(piece);
    },
  };
}

/**
 * A SHA-256 over a moment, computed as its chunks go up and saved at every
 * boundary.
 *
 * `hashedBytes` is how far the digest has consumed the file. The engine keeps
 * it equal to the server's committed offset: it feeds the hasher the SAME body
 * it just PATCHed (no second read), and catches up by reading when a resume
 * finds the two out of step.
 */
export class StreamingDigest {
  private finished: string | null = null;

  private constructor(
    private hasher: IHasher,
    public hashedBytes: number
  ) {}

  /**
   * Resume from a checkpoint, or start fresh. A checkpoint that will not load
   * (a different hash-wasm build, a truncated write) is not an error — it just
   * means the digest starts at zero and the caller catches it up, which costs
   * reads and nothing else.
   */
  static async create(checkpoint?: {
    hashedBytes: number;
    hashState?: Uint8Array;
  }): Promise<StreamingDigest> {
    const hasher = await createSHA256();
    if (checkpoint?.hashState && checkpoint.hashedBytes > 0) {
      try {
        // Copied, not passed through. `load()` refuses anything that is not an
        // `instanceof Uint8Array` of ITS realm, and a value that has been
        // structured-cloned out of IndexedDB is not always one — which turns a
        // resume that should cost nothing into a full re-read of the file, in
        // silence. 116 bytes; the copy is free and the guarantee is not.
        hasher.load(new Uint8Array(checkpoint.hashState));
        return new StreamingDigest(hasher, checkpoint.hashedBytes);
      } catch (err) {
        console.warn('[Queue] checksum checkpoint unusable — recomputing:', err);
      }
    }
    hasher.init();
    return new StreamingDigest(hasher, 0);
  }

  update(bytes: Uint8Array): void {
    if (this.finished) throw new Error('digest already finished');
    this.hasher.update(bytes);
    this.hashedBytes += bytes.byteLength;
  }

  /** The hasher's state, for the row's checkpoint. */
  save(): Uint8Array {
    return this.hasher.save();
  }

  /** Read forward until the digest has consumed `target` bytes. Used when a
   * resume adopts a server offset the checkpoint is behind — and, with no
   * checkpoint at all, to recompute the whole thing a chunk at a time. */
  async catchUp(source: ChunkSource, target: number): Promise<void> {
    while (this.hashedBytes < target) {
      const piece = await source.readAt(this.hashedBytes);
      if (!piece || piece.byteLength === 0) {
        throw new Error('media unreadable while computing its checksum');
      }
      const take =
        this.hashedBytes + piece.byteLength > target
          ? piece.subarray(0, target - this.hashedBytes)
          : piece;
      this.update(take);
    }
  }

  /** The hex digest. Cached: a finalize retried after a transient failure asks
   * for it again, and the hasher can only be finished once. */
  hex(): string {
    if (!this.finished) this.finished = this.hasher.digest('hex');
    return this.finished;
  }
}
